// Central registry of every sidebar item + every notification source.
// Drives the Sidebar rendering and the "Personalizar menú" settings page.
//
// Each menu item that can show a notification declares a fixed
// `notificationSource` key — the meaning isn't user-picked. The user only
// decides whether to show the item and whether to show its notification.
//
// Shape of users.menu_preferences:
// {
//   [menuKey]: {
//     visible: boolean,        // missing = visible (default ON)
//     notifications: boolean   // missing = default ON (if a source exists)
//   }
// }

import {
  HomeIcon,
  DeliveryIcon,
  PersonIcon,
  WrenchIcon,
  ShieldCheckMarkIcon,
  CashDollarIcon,
  ClipboardCheckFilledIcon,
  OrderFilledIcon,
  PersonFilledIcon,
  SettingsIcon,
  StoreIcon,
  AlertTriangleIcon,
  ChartLineIcon,
  TransferIcon,
  FileIcon,
  ReceiptIcon,
  ExportIcon,
  ImportIcon,
  BankIcon,
  ArrowDownIcon,
  ArrowUpIcon,
} from "@shopify/polaris-icons";
import supabase from "../supabase-client";
import { loadToSettle } from "../components/Traslados/settlementPreview";
import { MIN_LISTED_PAYMENT_CENTS } from "./payments";

// Titled groups for the Polaris navigation (the classic rail ignores them).
// Top-level items without a `section` render first, untitled.
export const MENU_SECTIONS = [
  { id: "operacion", title: "Operación" },
  { id: "facturacion", title: "Facturación" },
  { id: "finanzas", title: "Finanzas" },
  { id: "admin", title: "Administración" },
];

export const MENU_ITEMS = [
  { key: "home", label: "Dashboard", icon: HomeIcon, route: "/dashboard" },
  {
    key: "reservations",
    section: "operacion",
    label: "Reservaciones",
    icon: OrderFilledIcon,
    route: "/reservations",
    submenu: [
      { key: "reservations.list", label: "Todas", route: "/reservations" },
      { key: "reservations.create", label: "Crear", route: "/reservations/create" },
      { key: "reservations.timeline", label: "Ocupación", route: "/reservations/timeline" },
      { key: "reservations.cotizador", label: "Cotizador", route: "/cotizador", segment: "cp" },
    ],
  },
  {
    key: "customers",
    section: "operacion",
    label: "Clientes",
    icon: PersonFilledIcon,
    route: "/customers",
    submenu: [
      { key: "customers.persons", label: "Personas", route: "/customers" },
      { key: "customers.companies", label: "Empresas", route: "/companies" },
    ],
  },
  {
    key: "cars",
    section: "operacion",
    label: "Autos",
    icon: DeliveryIcon,
    route: "/cars",
    submenu: [
      { key: "cars.list", label: "Todos", route: "/cars" },
      { key: "cars.models", label: "Modelos", route: "/cars/models" },
      { key: "cars.brands", label: "Marcas", route: "/cars/brands" },
      { key: "cars.prices", label: "Precios", route: "/prices", segment: "cp" },
    ],
  },
  {
    key: "services",
    section: "operacion",
    label: "Servicios",
    icon: WrenchIcon,
    route: "/services_dashboard",
    submenu: [
      {
        key: "services.dashboard",
        label: "Dashboard",
        route: "/services_dashboard",
        notificationSource: "services_overdue",
      },
      {
        key: "services.list",
        label: "Servicios",
        route: "/services",
        notificationSource: "services_pending_action",
      },
      { key: "services.precheck", label: "¿Toca servicio?", route: "/services/precheck" },
      { key: "services.checklists", label: "Checklists", route: "/checklists" },
      {
        key: "services.washes",
        label: "Lavados",
        route: "/lavados",
        notificationSource: "washes_flagged",
      },
      {
        key: "services.pendingReports",
        label: "Reportes Pendientes",
        route: "/pending_reports",
        notificationSource: "pending_reports",
      },
      { key: "services.providers", label: "Proveedores", route: "/service_providers" },
      {
        key: "services.odometers",
        label: "Odometros",
        route: "/odometer_requests",
        notificationSource: "pending_odometers",
      },
    ],
  },
  {
    key: "traslados",
    section: "operacion",
    label: "Traslados",
    icon: TransferIcon,
    route: "/vehicle_transfers",
    submenu: [
      { key: "traslados.list", label: "Traslados", route: "/vehicle_transfers" },
      {
        key: "traslados.pendientes",
        label: "Pendientes",
        route: "/traslados_pendientes",
        notificationSource: "traslados_pendientes",
      },
      { key: "traslados.trasladistas", label: "Trasladistas", route: "/trasladistas" },
      { key: "traslados.casetasTag", label: "Casetas TAG", route: "/casetas_tag" },
      // Key kept from when it lived under Servicios so saved preferences apply.
      // Rates decide what we pay: only whoever authorizes payments sees them.
      {
        key: "services.trasladoRates",
        label: "Tarifas",
        route: "/traslado_rates",
        requiresPermission: "services.pay",
      },
    ],
  },
  { key: "insurance", label: "Pólizas", icon: ShieldCheckMarkIcon, route: "/insurance/all", section: "operacion" },
  {
    key: "infractions",
    section: "operacion",
    label: "Multas",
    icon: AlertTriangleIcon,
    route: "/infractions",
  },
  {
    key: "scannedDocuments",
    section: "operacion",
    label: "Documentos Escaneados",
    icon: FileIcon,
    route: "/documentos_escaneados",
    notificationSource: "scanned_documents_unlinked",
  },
  {
    key: "validaciones",
    section: "operacion",
    label: "Validaciones",
    icon: ClipboardCheckFilledIcon,
    route: "/validaciones",
    notificationSource: "pending_validaciones",
  },
  // Facturación = documents (CFDIs issued and received); Finanzas = money
  // (in, out, accounts). Both are listed straight under their section title in
  // the Polaris nav (flattenInNav); a row with `under` nests below that sibling
  // there. The classic rail shows each submenu flat, in this order.
  // Keys kept from the menus these lived in before, so saved preferences apply.
  {
    key: "facturacion",
    section: "facturacion",
    label: "Facturación",
    icon: ReceiptIcon,
    route: "/invoices",
    flattenInNav: true,
    submenu: [
      { key: "finance.invoices", label: "Emitidas", route: "/invoices", icon: ExportIcon },
      { key: "finance.createInvoice", label: "Nueva factura", route: "/invoicing/create", under: "finance.invoices" },
      { key: "facturacion.tracker", label: "Por cobrar", route: "/invoice_tracker", under: "finance.invoices" },
      { key: "reservations.autoInvoicing", label: "Automática", route: "/invoicing/auto", segment: "lp", under: "finance.invoices" },
      {
        key: "traslados.gastosFacturas",
        label: "Recibidas",
        route: "/gastos_facturas",
        icon: ImportIcon,
      },
    ],
  },
  {
    key: "finance",
    section: "finanzas",
    label: "Finanzas",
    icon: CashDollarIcon,
    route: "/payments/received",
    flattenInNav: true,
    submenu: [
      {
        key: "finance.receivedPayments",
        label: "Ingresos",
        route: "/payments/received",
        icon: ArrowDownIcon,
        notificationSource: "unapplied_payments",
      },
      { key: "finance.createPayment", label: "Registrar pago", route: "/payments/new", under: "finance.receivedPayments" },
      {
        key: "finance.egresos",
        label: "Egresos",
        route: "/egresos",
        icon: ArrowUpIcon,
        notificationSource: "egresos_por_comprobar",
      },
      {
        key: "finance.egresoRequests",
        label: "Solicitudes",
        route: "/egresos?tab=pendientes",
        under: "finance.egresos",
        requiresPermission: "services.pay",
        notificationSource: "egreso_requests",
      },
      {
        key: "finance.depositTransfers",
        label: "Devoluciones",
        route: "/deposit_transfers",
        under: "finance.egresos",
        notificationSource: "pending_deposit_transfers",
      },
      { key: "finance.fintocStatement", label: "Cuentas", route: "/payments/statement", icon: BankIcon },
      { key: "finance.cash", label: "Caja diaria", route: "/cash", under: "finance.fintocStatement" },
      { key: "finance.afirme", label: "Afirme", route: "/afirme_payments", under: "finance.fintocStatement" },
    ],
  },
  {
    key: "reports",
    section: "finanzas",
    label: "Reportes",
    icon: ChartLineIcon,
    route: "/reports",
  },
  {
    key: "users",
    section: "admin",
    label: "Usuarios",
    icon: PersonIcon,
    route: "/users",
    requiresRole: "superadmin",
  },
  {
    key: "settings.menu",
    section: "admin",
    label: "Personalizar menú",
    icon: SettingsIcon,
    route: "/settings/menu",
  },
  {
    key: "settings.company",
    section: "admin",
    label: "Ajustes de empresa",
    icon: StoreIcon,
    route: "/settings/company",
    requiresRole: "superadmin",
  },
];

// Flatten MENU_ITEMS into a list of configurable keys (top-level + submenu).
// Used by the settings page. Each row carries its fixed notificationSource
// (if any) so the UI can decide whether to show the notifications toggle.
export function enumerateMenuKeys() {
  const rows = [];
  for (const item of MENU_ITEMS) {
    rows.push({
      key: item.key,
      label: item.label,
      parent: null,
      notificationSource: item.notificationSource || null,
    });
    if (item.submenu) {
      for (const sub of item.submenu) {
        rows.push({
          key: sub.key,
          label: sub.label,
          parent: item.key,
          notificationSource: sub.notificationSource || null,
        });
      }
    }
  }
  return rows;
}

// ---------- Badge sources ----------
// Each source is an async fetcher returning a { count, tone } pair. The Sidebar
// calls every configured source once on mount and renders the returned count.
// When the "meaning" of a badge evolves, either add a new source here or
// change the fetcher body — nothing else needs to move.

// "Tickets por facturar": every ticket still waiting for its CFDI, including
// the ones the robot is working on.
const countTicketsToInvoice = () =>
  supabase
    .from("traslado_tickets")
    .select("id", { count: "exact", head: true })
    .or("status.eq.pending_cfdi,and(status.eq.approved,cfdi_pending.eq.true)");

export const BADGE_SOURCES = {
  traslados_pendientes: {
    // Everything on /traslados_pendientes: tickets waiting for their CFDI,
    // received CFDIs some ticket could take, tickets to approve, traslados with something to settle, settlements to pay.
    label: "Pendientes de traslados",
    fetch: async () => {
      const [toInvoice, review, settle, owed, cfdis] = await Promise.all([
        countTicketsToInvoice(),
        supabase
          .from("traslado_tickets")
          .select("id", { count: "exact", head: true })
          .eq("status", "needs_review"),
        supabase
          .from("traslado_settlements")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending_payment"),
        loadToSettle(),
        supabase
          .from("expense_cfdis")
          .select("id", { count: "exact", head: true })
          .eq("status", "unmatched")
          .eq("has_ticket_candidate", true),
      ]);
      const n =
        (toInvoice.count || 0) + (review.count || 0) + (settle.count || 0) + (owed.rows?.length || 0) + (cfdis.count || 0);
      return { count: n, tone: "attention" };
    },
  },
  unapplied_payments: {
    label: "Pagos por aplicar",
    fetch: async () => {
      const { count } = await supabase
        .from("payment_balances")
        .select("payment_id", { count: "exact", head: true })
        .gte("unapplied_cents", MIN_LISTED_PAYMENT_CENTS)
        .or("payer_type.is.null,payer_type.neq.payee");
      return { count: count || 0, tone: "attention" };
    },
  },
  egreso_requests: {
    // Payment requests waiting for whoever authorizes payments.
    label: "Solicitudes de pago",
    fetch: async () => {
      const { count } = await supabase
        .from("egresos")
        .select("id", { count: "exact", head: true })
        .eq("approval", "pendiente")
        .is("closed_at", null);
      return { count: count || 0, tone: "attention" };
    },
  },
  egresos_por_comprobar: {
    // Open egresos where money went out a week ago or more and nothing backs
    // it yet: an anticipo without ticket, change not returned, a deposit held.
    label: "Egresos por comprobar",
    fetch: async () => {
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
      const [{ data: open }, { data: bal }] = await Promise.all([
        supabase.from("egresos").select("id").is("closed_at", null),
        supabase
          .from("egreso_balances")
          .select("egreso_id")
          .gt("por_comprobar_cents", 0)
          .lt("last_movement_at", weekAgo),
      ]);
      const openIds = new Set((open || []).map((r) => r.id));
      return { count: (bal || []).filter((b) => openIds.has(b.egreso_id)).length, tone: "warning" };
    },
  },
  scanned_documents_unlinked: {
    label: "Documentos escaneados sin vincular",
    fetch: async () => {
      const { count } = await supabase
        .from("scanned_documents")
        .select("id", { count: "exact", head: true })
        .eq("status", "unlinked");
      return { count: count || 0, tone: "attention" };
    },
  },
  pending_validaciones: {
    label: "Validaciones pendientes",
    fetch: async () => {
      const { count } = await supabase
        .from("validaciones")
        .select("id", { count: "exact", head: true })
        .not("status", "in", "(approved,rejected)")
        .or("status.eq.pending,visited.eq.false");
      return { count: count || 0, tone: "critical" };
    },
  },
  pending_deposit_transfers: {
    label: "Transferencias pendientes",
    fetch: async () => {
      // Pending deposit returns + pending SPEI refunds — the two sources of the
      // unified "Transferencias Pendientes" queue.
      const [deposits, refunds] = await Promise.all([
        supabase
          .from("reservations")
          .select("id", { count: "exact", head: true })
          .eq("type", "rental")
          .eq("deposit_returned", true)
          .eq("deposit_transferred", false)
          .gt("deposit", 0),
        supabase
          .from("transactions")
          .select("id", { count: "exact", head: true })
          .eq("type", "refund")
          .eq("transferred", false),
      ]);
      const count = (deposits.count || 0) + (refunds.count || 0);
      return { count, tone: "warning" };
    },
  },
  // Cars with an overdue Aceite (oil) service — this is what renders red on
  // the Services Dashboard.
  services_overdue: {
    label: "Servicios vencidos",
    fetch: async () => {
      const { data } = await supabase.rpc("get_upcoming_services");
      const list = Array.isArray(data) ? data : [];
      const count = list.filter(
        (s) =>
          (s.kms_remaining ?? 0) < 0 &&
          (s.services_due || "").includes("Aceite")
      ).length;
      return { count, tone: "critical" };
    },
  },
  // Services awaiting admin action: unpaid or unapproved.
  services_pending_action: {
    label: "Servicios pendientes",
    fetch: async () => {
      // Anything on /services waiting on a human: approval, payment, or the CFDI
      // a provider still owes us after being paid. The badge turns critical once
      // an invoice has been outstanding for a month, so a "falta factura" queue
      // cannot quietly become permanent. A CFDI the provider cancelled at SAT
      // (invoice_status 'cancelled', usually already paid) is always critical.
      const overdueCutoff = new Date(
        Date.now() - 30 * 24 * 60 * 60 * 1000,
      ).toISOString();
      // One OR query, the same filter /services uses, so a service that is both
      // unapproved and missing its CFDI counts once. A service still
      // awaiting_provider owes no CFDI yet (the work isn't done), so it doesn't
      // count as "falta factura".
      const [pending, overdue, cancelled] = await Promise.all([
        supabase
          .from("services")
          .select("id", { count: "exact", head: true })
          .or(
            "status.in.(payment_pending,pending_approval)," +
              "and(invoice_status.eq.pending,status.neq.awaiting_provider)," +
              "invoice_status.eq.cancelled",
          ),
        supabase
          .from("services")
          .select("id", { count: "exact", head: true })
          .eq("invoice_status", "pending")
          .neq("status", "awaiting_provider")
          .lt("created_at", overdueCutoff),
        supabase
          .from("services")
          .select("id", { count: "exact", head: true })
          .eq("invoice_status", "cancelled"),
      ]);
      return {
        count: pending.count || 0,
        tone: overdue.count > 0 || cancelled.count > 0 ? "critical" : "warning",
      };
    },
  },
  // Negative checklist items not yet resolved.
  pending_reports: {
    label: "Reportes pendientes",
    fetch: async () => {
      const { count } = await supabase
        .from("car_checklist_items")
        .select("id", { count: "exact", head: true })
        .eq("answer", "negative")
        .or("dealt_with.is.null,dealt_with.eq.false");
      return { count: count || 0, tone: "critical" };
    },
  },
  // Car washes the AI flagged for a manager's decision.
  washes_flagged: {
    label: "Lavados por revisar",
    fetch: async () => {
      const { count } = await supabase
        .from("car_washes")
        .select("id", { count: "exact", head: true })
        .eq("review_status", "flagged");
      return { count: count || 0, tone: "warning" };
    },
  },
  // Odometer submissions awaiting admin review.
  pending_odometers: {
    label: "Odómetros por revisar",
    fetch: async () => {
      const { count } = await supabase
        .from("car_odometer_submissions")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending");
      return { count: count || 0, tone: "warning" };
    },
  },
};

// Flat lookup: key → its notificationSource (or null). Sidebar + settings use this.
export function getNotificationSource(menuKey) {
  for (const item of MENU_ITEMS) {
    if (item.key === menuKey) return item.notificationSource || null;
    if (item.submenu) {
      const sub = item.submenu.find((s) => s.key === menuKey);
      if (sub) return sub.notificationSource || null;
    }
  }
  return null;
}
